/**
 * Dev-only network tap to log Firebase Storage (and related) requests/responses.
 * Monkeypatches window.fetch and XMLHttpRequest when enabled.
 *
 * Enable with: VITE_DEBUG_NETWORK_TAP=true or NEXT_PUBLIC_DEBUG_NETWORK_TAP=true
 * Do not log auth tokens; Authorization and token query params are redacted.
 *
 * If 412 response body indicates App Check, see src/lib/firebase/appCheck.ts for
 * unblock steps (Firebase Console enforcement or ReCaptcha v3 + env).
 */

const DEBUG_NETWORK_TAP =
  (typeof import.meta !== "undefined" &&
    (import.meta as Record<string, unknown>).env &&
    ((import.meta as { env: Record<string, unknown> }).env.VITE_DEBUG_NETWORK_TAP === "true" ||
      (import.meta as { env: Record<string, unknown> }).env.NEXT_PUBLIC_DEBUG_NETWORK_TAP === "true")) ||
  (typeof process !== "undefined" && process.env?.NEXT_PUBLIC_DEBUG_NETWORK_TAP === "true");

function shouldTap(url: string): boolean {
  try {
    const u = url.toLowerCase();
    return (
      u.includes("firebasestorage.googleapis.com") ||
      u.includes("googleapis.com/upload/storage") ||
      u.includes("/v0/b/")
    );
  } catch {
    return false;
  }
}

function redactUrl(url: string): string {
  try {
    const u = new URL(url);
    const redactParams = ["access_token", "token", "oauth", "key"];
    u.searchParams.forEach((_, key) => {
      const lower = key.toLowerCase();
      if (redactParams.some((p) => lower.includes(p))) {
        u.searchParams.set(key, "[REDACTED]");
      }
    });
    return u.toString();
  } catch {
    return url.replace(/token=[^&]+/gi, "token=[REDACTED]").replace(/access_token=[^&]+/gi, "access_token=[REDACTED]");
  }
}

function redactHeaders(headers: Headers | Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  const iter = "entries" in headers ? (headers as Headers).entries() : Object.entries(headers);
  for (const [k, v] of iter) {
    const key = k.toLowerCase();
    if (key === "authorization" || key.includes("token")) {
      out[k] = "[REDACTED]";
    } else {
      out[k] = typeof v === "string" ? v : String(v);
    }
  }
  return out;
}

async function captureBody(res: Response): Promise<string> {
  try {
    const text = await res.text();
    return text.slice(0, 2000);
  } catch {
    return "";
  }
}

export function installNetworkTap(): void {
  if (typeof window === "undefined" || !DEBUG_NETWORK_TAP) return;

  const origFetch = window.fetch;
  window.fetch = async function tappedFetch(input: RequestInfo | URL, init?: RequestInit) {
    const url = typeof input === "string" ? input : input instanceof Request ? input.url : input.toString();
    if (!shouldTap(url)) {
      return origFetch.call(this, input, init);
    }

    const method = (init?.method ?? (typeof input !== "string" && input instanceof Request ? input.method : "GET")) as string;
    const res = await origFetch.call(this, input, init);
    const status = res.status;

    console.log("[NetworkTap] fetch", method, redactUrl(url), "status", status);

    if (status === 412 || status === 401 || status === 403) {
      const body = await captureBody(res.clone());
      console.error("[NetworkTap] fetch error body (412/401/403)", body);
      const headers = init?.headers ? redactHeaders(init.headers instanceof Headers ? init.headers : (init.headers as Record<string, string>)) : {};
      console.error("[NetworkTap] request headers (redacted)", headers);
    }

    return res;
  };

  const OrigXHR = window.XMLHttpRequest;
  const XHR = function (this: XMLHttpRequest) {
    const xhr = new OrigXHR();
    const origOpen = xhr.open;
    const origSend = xhr.send;
    let reqUrl = "";
    let reqMethod = "";

    xhr.open = function (method: string, url: string, ...rest: unknown[]) {
      reqMethod = method;
      reqUrl = url;
      return origOpen.apply(this, [method, url, ...rest] as [string, string, boolean?, string?, string?]);
    };

    xhr.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
      if (shouldTap(reqUrl)) {
        xhr.addEventListener("load", function () {
          console.log("[NetworkTap] XHR", reqMethod, redactUrl(reqUrl), "status", xhr.status);
          if (xhr.status === 412 || xhr.status === 401 || xhr.status === 403) {
            const text = typeof xhr.responseText === "string" ? xhr.responseText.slice(0, 2000) : "";
            console.error("[NetworkTap] XHR error body (412/401/403)", text);
          }
        });
      }
      return origSend.call(this, body);
    };

    return xhr;
  };
  window.XMLHttpRequest = XHR as typeof XMLHttpRequest;

  console.log("[NetworkTap] installed (fetch + XMLHttpRequest); tapping firebasestorage.googleapis.com, *.googleapis.com/upload/storage, /v0/b/");
}
